import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatErr, haversineKm, savePos } from "@/lib/api";
import { t } from "@/i18n";

const SIM_STEP_M = 600;

function cumulative(geom) {
  const c = [0];
  for (let i = 1; i < geom.length; i++) c.push(c[i - 1] + haversineKm(geom[i - 1], geom[i]) * 1000);
  return c;
}

function nearestIdx(geom, p, from = 0) {
  let best = from;
  let bd = Infinity;
  for (let i = from; i < geom.length; i++) {
    const d = (geom[i][0] - p[0]) ** 2 + (geom[i][1] - p[1]) ** 2;
    if (d < bd) {
      bd = d;
      best = i;
    }
  }
  return best;
}

const hitsRoute = (geom, a) => geom.some((p, i) => i % 3 === 0 && haversineKm(p, [a.lat, a.lng]) <= (a.radius_km || 2));

export function useNavigation({ trip, dest }) {
  const [pos, setPos] = useState(null);
  const [route, setRoute] = useState(null);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState(false);
  const [gps, setGps] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const seen = useRef(null);
  const stateRef = useRef({});
  stateRef.current = { pos, route, idx };

  const cum = useMemo(() => (route ? cumulative(route.geometry) : [0]), [route]);

  const calc = useCallback(async (from, reason) => {
    if (!from || !dest) return;
    setBusy(true);
    try {
      const { data } = await api.post("/route", { origin: from, destination: [dest.lat, dest.lng], vehicle: trip?.vehicle });
      setRoute(data);
      setIdx(0);
      if (data.rerouted) toast.success(t("Percorso ricalcolato: evitati {n} avvisi", { n: data.avoided.length }), { description: data.avoided.map((a) => t(a.title)).join(" · ") });
      else if (reason) toast(reason);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  }, [dest, trip]);

  const checkAlerts = useCallback(async () => {
    const { data } = await api.get("/alerts");
    const w = trip?.vehicle?.weight_t ?? 3.5;
    const list = data.filter((a) => w >= (a.min_weight_t || 0));
    setAlerts(list);
    const prev = seen.current;
    seen.current = new Set(list.map((a) => a.id));
    if (!prev) return;
    const { route: r, idx: i, pos: p } = stateRef.current;
    const fresh = list.filter((a) => !prev.has(a.id));
    const blocking = r ? fresh.filter((a) => hitsRoute(r.geometry.slice(i), a)) : [];
    if (blocking.length) {
      toast.warning(t("Avviso ANAS sul percorso: {title}", { title: t(blocking[0].title) }), { description: t("Ricalcolo automatico in corso…") });
      calc(p, null);
    }
  }, [trip, calc]);

  useEffect(() => {
    checkAlerts().catch(() => {});
    const id = setInterval(() => checkAlerts().catch(() => {}), 15000);
    return () => {
      clearInterval(id);
    };
  }, [checkAlerts]);

  useEffect(() => {
    if (!sim || !route) return undefined;
    let ticks = 0;
    const id = setInterval(() => {
      setIdx((i) => {
        let j = i;
        while (j < route.geometry.length - 1 && cum[j] - cum[i] < SIM_STEP_M) j++;
        const p = route.geometry[j];
        setPos(p);
        savePos(p);
        ticks += 1;
        if (trip && ticks % 15 === 0) api.post(`/trips/${trip.id}/position`, { lat: p[0], lng: p[1] }).catch(() => {});
        if (j >= route.geometry.length - 1) setSim(false);
        return j;
      });
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [sim, route, cum, trip]);

  useEffect(() => {
    if (!gps || !navigator.geolocation) return undefined;
    const id = navigator.geolocation.watchPosition(
      (g) => {
        const p = [g.coords.latitude, g.coords.longitude];
        setPos(p);
        savePos(p);
        const r = stateRef.current.route;
        if (r) setIdx(nearestIdx(r.geometry, p));
      },
      () => {
        toast.error(t("GPS non disponibile: uso la posizione simulata"));
        setGps(false);
      },
      { enableHighAccuracy: true },
    );
    return () => {
      navigator.geolocation.clearWatch(id);
    };
  }, [gps]);

  const simulateAlert = async () => {
    if (!route) return;
    const g = route.geometry;
    const j = Math.min(g.length - 1, idx + Math.floor((g.length - idx) * 0.3));
    await api.post("/alerts/simulate", { lat: g[j][0], lng: g[j][1] });
    toast(t("Nuovo avviso ANAS ricevuto (simulato)"));
    await checkAlerts();
  };

  const step = route?.steps.find((s) => s.idx > idx) || route?.steps[route.steps.length - 1];
  const total = cum[cum.length - 1] || 1;
  const remainingM = Math.max(0, total - (cum[idx] || 0));
  const nav = {
    step,
    distToStep: step ? Math.max(0, cum[step.idx] - cum[idx]) : 0,
    remainingM,
    remainingS: route ? (route.duration_s * remainingM) / total : 0,
  };

  return { pos, setPos, route, idx, busy, sim, setSim, gps, setGps, alerts, calc, simulateAlert, nav };
}
